import { normalizeConfigSettingsFromApi } from '@/utils/maintenance-mode';
import { PUBLIC_RESOURCE_URL_PATH_PATTERN } from '@/shared/constants/custom-styles';
import { getHelixApiBaseUrl, resolveApiOrigin } from '@/utils/api-url';
import {
  EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY,
  configureExternalDomainAllowlist,
  isAllowlistedExternalUrl,
} from '@/utils/external-domain-allowlist';

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{3,8}$/;

const FONT_FAMILY_UNSAFE_PATTERN = /[<>;]|url\(/i;

export { PUBLIC_RESOURCE_URL_PATH_PATTERN } from '@/shared/constants/custom-styles';

export const BRANDING_COLOR_KEYS = [
  'app_color_primary',
  'app_color_secondary',
  'app_color_toolbar',
  'app_color_error',
  'app_color_success',
  'app_color_warning',
  'app_color_info',
  'app_color_background',
  'app_color_surface',
] as const;

export const BRANDING_PASS_THROUGH_KEYS = ['app_theme', 'app_default_workspace'] as const;

/** Branding keys normalized by normalizeAllApiSettings before merge. */
export const BRANDING_SETTING_KEYS = [
  ...BRANDING_COLOR_KEYS,
  ...BRANDING_PASS_THROUGH_KEYS,
  'app_font_url',
  'app_font_family',
  'app_logo_url',
] as const;

const CITIZEN_MODULE_PREFIX = 'citizen_module_';

/**
 * Nested module behavior keys accepted as API object settings.
 * Non-object values on these keys must be stripped before merge — see ADR-0004.
 * Runtime enforcement lives in stripConflictingModuleSettings() below.
 */
export const NESTED_MODULE_SETTING_KEYS = [
  `${CITIZEN_MODULE_PREFIX}my_cases`,
  `${CITIZEN_MODULE_PREFIX}my_children`,
  `${CITIZEN_MODULE_PREFIX}my_eservices`,
  `${CITIZEN_MODULE_PREFIX}my_associations`,
  `${CITIZEN_MODULE_PREFIX}my_educations`,
] as const;

let configuredNestedModuleSettingKeys: readonly string[] = NESTED_MODULE_SETTING_KEYS;

/** Supply module IDs from the app (e.g. MODULE_REGISTRY) to keep one source of truth. */
export function configureNestedModuleSettingKeys(moduleIds: readonly string[]): void {
  configuredNestedModuleSettingKeys = moduleIds.map((id) => `${CITIZEN_MODULE_PREFIX}${id}`);
}

function moduleRoutingKey(moduleId: string, suffix: 'enabled' | 'path') {
  return `${CITIZEN_MODULE_PREFIX}${moduleId}_${suffix}`;
}

export type BrandingSettingKey = (typeof BRANDING_SETTING_KEYS)[number];

/** Returns true when `value` is a valid #RGB/#RRGGBB hex color string. */
export function isValidHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_REGEX.test(value);
}

/** Returns true for an API public resource path (no scheme, no host). */
export function isPublicResourcePath(value: unknown): boolean {
  return typeof value === 'string' && (value.startsWith('/api/v2/public/resources/') || value.startsWith('/public/resources/'));
}

/**
 * Strict path-only URL for settings that must never be absolute (auth redirects, module paths).
 *
 * Accepts a root-relative path or a bare filename. Rejects schemes, protocol-relative forms
 * (`//…`, `/\…`), backslashes, `..` segments, whitespace, and control characters.
 */
function hasAsciiControlOrDel(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);

    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }

  return false;
}

export function isPathOnlySettingUrl(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();

  if (!trimmed || trimmed !== value) {
    // Leading/trailing whitespace is rejected (trim changed the value).
    return false;
  }

  if (/\s/.test(trimmed) || trimmed.includes('\\') || hasAsciiControlOrDel(trimmed)) {
    return false;
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return false;
  }

  if (trimmed.startsWith('//')) {
    return false;
  }

  if (trimmed.split('/').includes('..')) {
    return false;
  }

  if (trimmed.startsWith('/')) {
    return true;
  }

  // Bare filename — no path separators.
  return /^[\w.-]+$/.test(trimmed);
}

/**
 * Auth redirects from settings are never absolute, allowlist or not.
 * Returns the path when valid; otherwise warns and returns undefined so env fallbacks apply.
 */
export function normalizeAuthRedirectSetting(key: string, value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  if (typeof value === 'string' && isPathOnlySettingUrl(value) && value.startsWith('/')) {
    return value.replace(/\/$/, '') || value;
  }

  console.warn(`[settings] Ignoring invalid auth redirect "${key}" (path-only; never absolute):`, value);
  return undefined;
}

/**
 * Returns true for an API public resource path, or an https URL on a hostname listed
 * in DEFAULT_EXTERNAL_DOMAINS or app_external_domain_allowlist.
 */
export function isAllowlistedFontUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  return isPublicResourcePath(value) || isAllowlistedExternalUrl(value);
}

/**
 * Returns true for an API public resource path, a public resource URL on the API's own
 * origin, or an https URL on an allowlisted hostname.
 */
export function isAllowlistedResourceUrl(value: unknown, apiBaseUrl?: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  if (isPublicResourcePath(value)) {
    return true;
  }

  if (isAllowlistedExternalUrl(value)) {
    return true;
  }

  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    return false;
  }

  try {
    const url = new URL(value);
    const apiOrigin = resolveApiOrigin(apiBaseUrl ?? getHelixApiBaseUrl());

    if (!apiOrigin) {
      return false;
    }

    return url.origin === apiOrigin && PUBLIC_RESOURCE_URL_PATH_PATTERN.test(url.pathname);
  } catch {
    return false;
  }
}

function isValidFontFamily(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  return !FONT_FAMILY_UNSAFE_PATTERN.test(value);
}

function coerceOptionalString(value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  return String(value).trim() || undefined;
}

function warnInvalidBrandingSetting(key: BrandingSettingKey, value: unknown) {
  console.warn(`[settings] Ignoring invalid branding setting "${key}":`, value);
}

/** Normalizes a single branding setting value; returns undefined when invalid. */
export function normalizeBrandingSettingValue(key: BrandingSettingKey, value: unknown): string | undefined {
  if ((BRANDING_COLOR_KEYS as readonly string[]).includes(key)) {
    if (isValidHexColor(value)) {
      return value;
    }

    if (value != null && value !== '') {
      warnInvalidBrandingSetting(key, value);
    }

    return undefined;
  }

  if (key === 'app_font_url') {
    if (isAllowlistedFontUrl(value)) {
      return value;
    }

    if (value != null && value !== '') {
      warnInvalidBrandingSetting(key, value);
    }

    return undefined;
  }

  if (key === 'app_logo_url') {
    if (isAllowlistedResourceUrl(value)) {
      return value;
    }

    if (value != null && value !== '') {
      warnInvalidBrandingSetting(key, value);
    }

    return undefined;
  }

  if (key === 'app_font_family') {
    if (isValidFontFamily(value)) {
      return value.trim();
    }

    if (value != null && value !== '') {
      warnInvalidBrandingSetting(key, value);
    }

    return undefined;
  }

  return coerceOptionalString(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Hub exposes `_beta_mode` as boolean on current APIs and as a string on some older payloads. */
export function normalizeBetaModeValue(value: unknown): boolean {
  return value === true || value === 'true';
}

function warnStrippedModuleSetting(key: string, value: unknown, reason: string) {
  console.warn(`[settings] Ignoring API setting "${key}" (${reason}):`, value);
}

function normalizeModuleRoutingKey(result: Record<string, unknown>, moduleId: string, suffix: 'enabled' | 'path'): void {
  const routingKey = moduleRoutingKey(moduleId, suffix);

  if (!(routingKey in result)) {
    return;
  }

  const value = result[routingKey];

  if (suffix === 'path') {
    if (typeof value === 'string' && isPathOnlySettingUrl(value) && value.startsWith('/')) {
      result[routingKey] = value.replace(/\/$/, '') || value;
      return;
    }

    warnStrippedModuleSetting(routingKey, value, 'path must be a root-relative path with no host or scheme');
    delete result[routingKey];
    return;
  }

  if (typeof value === 'boolean') {
    return;
  }

  if (value === 'true' || value === 'false') {
    result[routingKey] = value === 'true';
    return;
  }

  if (value === 0 || value === '0') {
    result[routingKey] = false;
    return;
  }

  if (value === 1 || value === '1') {
    result[routingKey] = true;
    return;
  }

  warnStrippedModuleSetting(routingKey, value, 'enabled must be boolean');
  delete result[routingKey];
}

function warnUnknownModuleSettings(settings: Record<string, unknown>) {
  const knownIds = new Set(configuredNestedModuleSettingKeys.map((key) => key.slice(CITIZEN_MODULE_PREFIX.length)));

  for (const key of Object.keys(settings)) {
    if (!key.startsWith(CITIZEN_MODULE_PREFIX)) {
      continue;
    }

    const rest = key.slice(CITIZEN_MODULE_PREFIX.length);
    let moduleId = rest;

    if (rest.endsWith('_enabled')) {
      moduleId = rest.slice(0, -'_enabled'.length);
    } else if (rest.endsWith('_path')) {
      moduleId = rest.slice(0, -'_path'.length);
    }

    if (!knownIds.has(moduleId)) {
      console.warn(`[settings] Ignoring unknown module setting "${key}" (module "${moduleId}" is not registered); skipped.`);
    }
  }
}

/**
 * Preserves API-provided module behavior objects and strips invalid primitive
 * values so shallow merge cannot replace the object contract. Validates the
 * independent routing path/enabled suffix keys.
 */
export function stripConflictingModuleSettings(settings: Record<string, unknown>): Record<string, unknown> {
  const result = { ...settings };

  warnUnknownModuleSettings(result);

  for (const key of configuredNestedModuleSettingKeys) {
    if (!(key in result)) {
      continue;
    }

    if (!isPlainObject(result[key])) {
      warnStrippedModuleSetting(key, result[key], 'expected an API object setting; primitive values are not valid on this key');
      delete result[key];
    }
  }

  for (const nestedKey of configuredNestedModuleSettingKeys) {
    const moduleId = nestedKey.slice(CITIZEN_MODULE_PREFIX.length);
    normalizeModuleRoutingKey(result, moduleId, 'enabled');
    normalizeModuleRoutingKey(result, moduleId, 'path');
  }

  return result;
}

/** Normalizes API settings (maintenance, module routing, branding) before merge. */
export function normalizeAllApiSettings<T extends Record<string, unknown>>(settings: T): T {
  const normalized = normalizeConfigSettingsFromApi(settings) as Record<string, unknown>;
  const result = stripConflictingModuleSettings(normalized);

  if ('_beta_mode' in result) {
    result._beta_mode = normalizeBetaModeValue(result._beta_mode);
  }

  // Must run before the branding loop: those URL checks read this allowlist. When the
  // key is absent the previously configured value stands, so an allowlist established
  // during public bootstrap survives into the authenticated settings pass.
  if (EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY in result) {
    const domains = configureExternalDomainAllowlist(result[EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY]);

    if (domains.length) {
      result[EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY] = domains.join(',');
    } else {
      delete result[EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY];
    }
  }

  for (const key of BRANDING_SETTING_KEYS) {
    if (!(key in result)) {
      continue;
    }

    const normalizedValue = normalizeBrandingSettingValue(key, result[key]);

    if (normalizedValue === undefined) {
      delete result[key];
      continue;
    }

    result[key] = normalizedValue;
  }

  return result as T;
}
